"use client"

import * as React from "react"
import { useSearchParams } from "next/navigation"
import { LogIn, AlertCircle } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

function LoginForm() {
  const searchParams = useSearchParams()
  // Nur app-interne Pfade zulassen — sonst offene Weiterleitung bzw.
  // `javascript:`-Ausführung über ?next=…
  const rawNext = searchParams.get("next") ?? ""
  const nextPath =
    rawNext.startsWith("/") && !rawNext.startsWith("//") && !rawNext.startsWith("/\\")
      ? rawNext
      : "/dashboard"

  const [email, setEmail] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // Wenn die Instanz noch keinen User hat, gibt es nichts zum Anmelden →
  // direkt zur Erst-Registrierung weiterleiten.
  React.useEffect(() => {
    fetch("/api/auth/needs-setup")
      .then((r) => r.json())
      .then((data) => {
        if (data?.needsSetup) window.location.href = "/register"
      })
      .catch(() => {})
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(data?.error ?? "Anmeldung fehlgeschlagen.")
        return
      }
      window.location.href = nextPath
    } catch {
      setError("Netzwerkfehler. Bitte erneut versuchen.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
          E-Mail
        </label>
        <Input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="office@tiles.at"
          required
          autoFocus
        />
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
          Passwort
        </label>
        <Input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </div>
      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? "Anmelden…" : "Anmelden"}
      </Button>
    </form>
  )
}

export default function LoginPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <LogIn className="size-5 text-slate-400" />
            Anmelden
          </CardTitle>
          <p className="text-sm text-slate-500">Leadscraper — Zugang nur für authentifizierte Nutzer.</p>
        </CardHeader>
        <CardContent>
          <React.Suspense fallback={<div className="h-48" />}>
            <LoginForm />
          </React.Suspense>
        </CardContent>
      </Card>
    </div>
  )
}
